import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTheme } from '../../assets/contexts/ThemeContext';
import { useAuth } from '../../assets/contexts/AuthContext';
import useMenuData from '../../assets/hooks/useMenuData';
import { menuTreeHasThemeManagementAccess } from '../../assets/utils/theme/themeManagementMenuAccess';
import { ThemeConfig } from '../../assets/utils/theme/themeConfig';
import { ThemeHelpHighlight } from '../../assets/utils/theme/themeHelpHighlight';
import './ThemeRemoteControl.css';

// 컨트롤 컴포넌트들 import
import HeaderTopControl from './controls/HeaderTopControl';
import NavLayoutControl from './controls/NavLayoutControl';
import SubmenuTypeControl from './controls/SubmenuTypeControl';
import MenuScrollControl from './controls/MenuScrollControl';
import MenuAlignControl from './controls/MenuAlignControl';
import HeaderBottomControl from './controls/HeaderBottomControl';
import HeaderLogoControl from './controls/HeaderLogoControl';
import BannerTypeControl from './controls/BannerTypeControl';
import FooterControl from './controls/FooterControl';
import FontControl from './controls/FontControl';
import ColorControl from './controls/ColorControl';
import GridControl from './controls/GridControl';
import ContainerWidthControl from './controls/ContainerWidthControl';
import AreaContainerWidthControl from './controls/AreaContainerWidthControl';
import MegaMenuSectionMaxColumnsControl from './controls/MegaMenuSectionMaxColumnsControl';
import MegaMenuColumnsControl from './controls/MegaMenuColumnsControl';
import MegaMenuStyleControl from './controls/MegaMenuStyleControl';
import SubmenuSidebarControl from './controls/SubmenuSidebarControl';
import RightSidebarTypeControl from './controls/RightSidebarTypeControl';
import ButtonThemeControl from './controls/ButtonThemeControl';
import AOSControl from './controls/AOSControl';
import RelatedSitesControl from './controls/RelatedSitesControl';
import ScrollToTopButtonControl from './controls/ScrollToTopButtonControl';
import FooterEnabledControl from './controls/FooterEnabledControl';

/** 테스트 모드: URL에 ?themeControl=1 이 있거나 localStorage.showThemeControl === '1' 일 때만 노출 (기본 비노출) */
const isThemeControlTestMode = () => {
  if (typeof window === 'undefined') return false;
  const byUrl = new URLSearchParams(window.location.search).get('themeControl') === '1';
  const byStorage = localStorage.getItem('showThemeControl') === '1';
  return byUrl || byStorage;
};

const ThemeRemoteControl = () => {
  const themeContext = useTheme();
  const auth = useAuth();
  const { menuData, menuLoading } = useMenuData({ skip: false });
  const [activeTab, setActiveTab] = useState('site-common');
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [isMinimized, setIsMinimized] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const [position, setPosition] = useState({ top: 10, right: 10 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const showControl = useMemo(() => {
    if (!themeContext) return false;
    if (isThemeControlTestMode()) return true;
    if (!auth?.isAuthenticated) return false;
    if (menuLoading) return false;
    if (!menuTreeHasThemeManagementAccess(menuData)) return false;
    const cfg = themeContext.themeConfig || {};
    return cfg.themeRemoteControlVisible === true;
  }, [themeContext, auth?.isAuthenticated, menuLoading, menuData]);

  const wrapperRef = useRef(null);
  const headerRef = useRef(null);
  const contentRef = useRef(null);

  // 아코디언 자동 스크롤 (원본 initAccordionAutoScroll 로직)
  useEffect(() => {
    if (!activeAccordion || isMinimized) return; // 아코디언이 열리지 않았거나 최소화 상태면 스킵

    const scrollContainer = contentRef.current?.querySelector('.tab-content');
    if (!scrollContainer) return;

    // 아코디언 아이템 찾기 (activeAccordion ID로)
    const accordionItem = scrollContainer.querySelector(`.accordion-item[data-accordion-id="${activeAccordion}"]`);
    if (!accordionItem) return;

    // 스크롤 컨테이너 내에서 아코디언 아이템의 위치 계산
    const itemOffsetTop = accordionItem.offsetTop;
    const tabNavHeight = scrollContainer.previousElementSibling ? scrollContainer.previousElementSibling.offsetHeight : 0;

    // 약간의 여백을 두고 스크롤 (상단 여백 10px)
    const targetScrollTop = itemOffsetTop - tabNavHeight - 10;

    // 부드러운 스크롤 (약간의 지연을 두어 아코디언 애니메이션 완료 후 스크롤)
    const timer = setTimeout(() => {
      scrollContainer.scrollTo({
        top: Math.max(0, targetScrollTop),
        behavior: 'smooth'
      });
    }, 100); // collapse 애니메이션 완료를 위한 약간의 지연

    return () => {
      clearTimeout(timer);
    };
  }, [activeAccordion, isMinimized]);

  // 물음표 버튼 하이라이트 효과 초기화 (React 모듈 사용)
  useEffect(() => {
    if (isMinimized || !contentRef.current) return;

    // DOM이 업데이트된 후 초기화 (여러 프레임 대기하여 아코디언 애니메이션 완료 후 실행)
    const timer = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (ThemeHelpHighlight && contentRef.current) {
          // 재초기화 (initHelpButtons 내부에서 중복 방지 처리)
          ThemeHelpHighlight.initHelpButtons(contentRef.current);
        }
      });
    });

    return () => {
      cancelAnimationFrame(timer);
    };
  }, [isMinimized, activeTab, activeAccordion]);

  // 최대화/최소화 시 위치 재계산 (화면 밖으로 나가지 않도록)
  useEffect(() => {
    if (!wrapperRef.current) return;

    // DOM 업데이트 후 위치 재계산을 위해 약간의 지연
    const timer = setTimeout(() => {
      const rect = wrapperRef.current.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;

      // 현재 DOM 위치를 직접 가져옴
      const currentLeft = rect.left;
      const currentTop = rect.top;

      let newLeft = currentLeft;
      let newTop = currentTop;

      // 화면 밖으로 나간 경우 위치 조정
      if (newLeft < 0) newLeft = 0;
      if (newLeft > maxX) newLeft = Math.max(0, maxX);
      if (newTop < 0) newTop = 0;
      if (newTop > maxY) newTop = Math.max(0, maxY);

      // 위치가 변경된 경우에만 업데이트
      if (newLeft !== currentLeft || newTop !== currentTop) {
        setPosition({ top: newTop, right: 'auto', left: newLeft });
      }
    }, 50); // CSS transition 완료를 위한 약간의 지연

    return () => clearTimeout(timer);
  }, [isMinimized]);

  // 윈도우 리사이즈 시 위치 재계산
  useEffect(() => {
    const handleResize = () => {
      if (!wrapperRef.current) return;

      const rect = wrapperRef.current.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;

      // 현재 position 상태를 직접 참조하지 않고 DOM에서 가져옴
      const currentLeft = rect.left;
      const currentTop = rect.top;

      let newLeft = currentLeft;
      let newTop = currentTop;

      // 화면 밖으로 나간 경우 위치 조정
      if (newLeft < 0) newLeft = 0;
      if (newLeft > maxX) newLeft = Math.max(0, maxX);
      if (newTop < 0) newTop = 0;
      if (newTop > maxY) newTop = Math.max(0, maxY);

      // 위치가 변경된 경우에만 업데이트
      if (newLeft !== currentLeft || newTop !== currentTop) {
        setPosition({ top: newTop, right: 'auto', left: newLeft });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 드래그 핸들러
  useEffect(() => {
    if (!themeContext) return;
    const headerElement = headerRef.current;
    if (!headerElement) return;

    const handleMouseDown = (e) => {
      if (e.target.closest('button') || e.target.closest('select') || e.target.closest('input')) {
        return;
      }
      if (e.target.closest('.tab-content')) return;

      setIsDragging(true);
      const rect = wrapperRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    };

    const handleMouseMove = (e) => {
      if (!isDragging) return;
      e.preventDefault();

      let newLeft = e.clientX - dragOffset.x;
      let newTop = e.clientY - dragOffset.y;

      const rect = wrapperRef.current.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width;
      const maxY = window.innerHeight - rect.height;

      if (newLeft < 0) newLeft = 0;
      if (newLeft > maxX) newLeft = maxX;
      if (newTop < 0) newTop = 0;
      if (newTop > maxY) newTop = maxY;

      setPosition({ top: newTop, right: 'auto', left: newLeft });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    headerElement.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      if (headerElement) {
        headerElement.removeEventListener('mousedown', handleMouseDown);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, themeContext]);

  if (!themeContext) return null;

  const { themeConfig = {} } = themeContext;

  // 닫기 버튼 핸들러 (원본과 동일)
  const handleClose = (e) => {
    e.stopPropagation();
    setIsClosed(true);
    if (wrapperRef.current) {
      wrapperRef.current.style.display = 'none';
    }
  };

  const toggleAccordion = (id) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  const handleSave = async () => {
    try {
      const success = await ThemeConfig.saveToDB();
      if (success) {
        alert('테마 설정이 성공적으로 저장되었습니다.');
      } else {
        alert('테마 설정 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('테마 저장 중 예외 발생:', error);
      alert('테마 저장 중 오류가 발생했습니다.');
    }
  };

  if (!showControl) {
    return null;
  }

  // 닫혔으면 렌더링하지 않음
  if (isClosed) {
    return null;
  }

  return (
    <>
      {/* 테마 제어 패널 (항상 표시) */}
    <div
      id="theme-control-wrapper"
      ref={wrapperRef}
      className={isMinimized ? 'minimized' : ''}
      style={{
        top: position.top,
        right: position.right === 'auto' ? 'auto' : position.right,
        left: position.left,
        border: `2px solid ${themeConfig.themeColor || '#e97730'}`,
        height: isMinimized ? 'auto' : '500px'
      }}
    >
      <input type="text" id="theme-control-scroll-input" tabIndex={-1} />

      {/* 드래그 가능한 헤더 */}
      <div
        id="theme-control-header"
        ref={headerRef}
        style={{
          background: themeConfig.themeColor || '#e97730',
          cursor: isDragging ? 'grabbing' : 'move'
        }}
      >
        <span className="theme-control-header-title">🎨 테마 원격 제어기</span>
        <div className="theme-control-header-buttons">
          <button
            id="theme-control-minimize-btn"
            type="button"
            title={isMinimized ? '원래대로' : '최소화'}
            onClick={() => setIsMinimized(!isMinimized)}
          >
            {isMinimized ? '□' : '−'}
          </button>
          <button
            id="theme-control-close-btn"
            type="button"
            title="닫기"
            onClick={handleClose}
          >
            ×
          </button>
        </div>
      </div>

      {/* 콘텐츠 영역 */}
      <div
        id="theme-control-content"
        ref={contentRef}
        className={isMinimized ? 'minimized' : ''}
        style={{
          display: isMinimized ? 'none' : 'flex',
          height: isMinimized ? 0 : 'calc(500px - 40px)'
        }}
      >
        {/* 탭 헤더 */}
        <div className="theme-control-tab-header">
          <ul className="nav nav-tabs" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'site-common' ? 'active' : ''}`}
                onClick={() => setActiveTab('site-common')}
                style={{
                  color: activeTab === 'site-common' ? '#333' : '#666',
                  borderBottom: activeTab === 'site-common' ? `2px solid ${themeConfig.themeColor || '#e97730'}` : '2px solid transparent'
                }}
              >
                영역별 설정
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link ${activeTab === 'overall-common' ? 'active' : ''}`}
                onClick={() => setActiveTab('overall-common')}
                style={{
                  color: activeTab === 'overall-common' ? '#333' : '#666',
                  borderBottom: activeTab === 'overall-common' ? `2px solid ${themeConfig.themeColor || '#e97730'}` : '2px solid transparent'
                }}
              >
                전체 설정
              </button>
            </li>
          </ul>
          <button
            id="theme-control-save-btn"
            type="button"
            onClick={handleSave}
            style={{
              background: themeConfig.themeColor || '#e97730'
            }}
          >
            적용
          </button>
        </div>

        {/* 탭 콘텐츠 */}
        <div className="tab-content">
          {activeTab === 'site-common' && (
            <div className="accordion" id="site-common-accordion">
              {/* 최상단 영역 */}
              <div className="accordion-item" data-accordion-id="header-top">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeAccordion === 'header-top' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleAccordion('header-top')}
                  >
                    최상단 영역
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${activeAccordion === 'header-top' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    {/* 최상단 영역 표시(토글)를 최상단으로 이동 */}
                    <HeaderTopControl />

                    {/* 표시가 꺼져있으면 컨테이너 너비/스타일 설정은 숨김 */}
                    {themeConfig.headerTopVisible !== false && (
                      <div style={{ marginBottom: '15px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <label style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>📐 컨테이너 너비:</label>
                          <button
                            type="button"
                            onClick={() => {
                              const { set } = themeContext;
                              const areaVarNameMap = {
                                'header-top': '--container-max-width-header-top',
                                'header-bottom': '--container-max-width-header-bottom',
                                'banner': '--container-max-width-banner',
                                'content': '--container-max-width-content',
                                'footer': '--container-max-width-footer'
                              };
                              const varPrefix = areaVarNameMap['header-top'] || '--container-max-width-header-top';

                              // 각 사이즈별로 기본값으로 리셋
                              ['xl', 'lg', 'md', 'sm'].forEach(size => {
                                const varName = `${varPrefix}-${size}`;
                                document.documentElement.style.removeProperty(varName);
                                set(`areaContainerWidths.header-top.${size}`, null);
                              });
                            }}
                            style={{
                              background: 'transparent',
                              border: '1px solid #ddd',
                              color: '#666',
                              fontSize: '10px',
                              padding: '2px 8px',
                              borderRadius: '3px',
                              cursor: 'pointer'
                            }}
                            onMouseOver={(e) => {
                              e.target.style.backgroundColor = '#f5f5f5';
                              e.target.style.borderColor = themeConfig.themeColor || '#e97730';
                            }}
                            onMouseOut={(e) => {
                              e.target.style.backgroundColor = 'transparent';
                              e.target.style.borderColor = '#ddd';
                            }}
                          >
                            리셋
                          </button>
                        </div>
                        <AreaContainerWidthControl areaKey="header-top" areaName="최상단 영역" areaSelector=".header-top" areaNumber={1} />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 상단 로고 영역 */}
              <div className="accordion-item" data-accordion-id="header-logo">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeAccordion === 'header-logo' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleAccordion('header-logo')}
                  >
                    상단 로고 영역
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${activeAccordion === 'header-logo' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <HeaderLogoControl />
                  </div>
                </div>
              </div>

              {/* 상단 메인메뉴 영역 */}
              <div className="accordion-item" data-accordion-id="header-menu">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeAccordion === 'header-menu' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleAccordion('header-menu')}
                  >
                    상단 메인메뉴 영역
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${activeAccordion === 'header-menu' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <NavLayoutControl />
                    {/* 상단(Top) 배치일 때만 표시 */}
                    {themeConfig.navLayout === 'top' && (
                      <>
                        <SubmenuTypeControl />
                        <MenuScrollControl />
                        <MenuAlignControl />
                        <HeaderBottomControl />
                        <div style={{ marginTop: '15px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>📐 컨테이너 너비:</label>
                            <button
                              type="button"
                              onClick={() => {
                                const { set } = themeContext;
                                const areaVarNameMap = {
                                  'header-top': '--container-max-width-header-top',
                                  'header-bottom': '--container-max-width-header-bottom',
                                  'banner': '--container-max-width-banner',
                                  'content': '--container-max-width-content',
                                  'footer': '--container-max-width-footer'
                                };
                                const varPrefix = areaVarNameMap['header-bottom'] || '--container-max-width-header-bottom';

                                // 각 사이즈별로 기본값으로 리셋
                                ['xl', 'lg', 'md', 'sm'].forEach(size => {
                                  const varName = `${varPrefix}-${size}`;
                                  document.documentElement.style.removeProperty(varName);
                                  set(`areaContainerWidths.header-bottom.${size}`, null);
                                });
                              }}
                              style={{
                                background: 'transparent',
                                border: '1px solid #ddd',
                                color: '#666',
                                fontSize: '10px',
                                padding: '2px 8px',
                                borderRadius: '3px',
                                cursor: 'pointer'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#f5f5f5';
                                e.target.style.borderColor = themeConfig.themeColor || '#e97730';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.borderColor = '#ddd';
                              }}
                            >
                              리셋
                            </button>
                          </div>
                          <AreaContainerWidthControl areaKey="header-bottom" areaName="메뉴 영역" areaSelector=".header-bottom" areaNumber={2} />
                        </div>
                      </>
                    )}
                    {/* 상단(Top) 배치일 때만 메가메뉴 스타일 표시 */}
                    {themeConfig.navLayout === 'top' && (
                      <MegaMenuStyleControl />
                    )}
                    <MegaMenuSectionMaxColumnsControl />
                    <MegaMenuColumnsControl />
                  </div>
                </div>
              </div>

              {/* 업무화면 제목 배너 영역 */}
              <div className="accordion-item" data-accordion-id="banner">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeAccordion === 'banner' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleAccordion('banner')}
                  >
                    업무화면 제목 배너 영역
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${activeAccordion === 'banner' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <BannerTypeControl />
                    <div style={{ marginTop: '15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>📐 컨테이너 너비:</label>
                        <button
                          type="button"
                          onClick={() => {
                            const { set } = themeContext;
                            const areaVarNameMap = {
                              'header-top': '--container-max-width-header-top',
                              'header-bottom': '--container-max-width-header-bottom',
                              'banner': '--container-max-width-banner',
                              'content': '--container-max-width-content',
                              'footer': '--container-max-width-footer'
                            };
                            const varPrefix = areaVarNameMap['banner'] || '--container-max-width-banner';

                            // 각 사이즈별로 기본값으로 리셋
                            ['xl', 'lg', 'md', 'sm'].forEach(size => {
                              const varName = `${varPrefix}-${size}`;
                              document.documentElement.style.removeProperty(varName);
                              set(`areaContainerWidths.banner.${size}`, null);
                            });
                          }}
                          style={{
                            background: 'transparent',
                            border: '1px solid #ddd',
                            color: '#666',
                            fontSize: '10px',
                            padding: '2px 8px',
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = '#f5f5f5';
                            e.target.style.borderColor = themeConfig.themeColor || '#e97730';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.borderColor = '#ddd';
                          }}
                        >
                          리셋
                        </button>
                      </div>
                      <AreaContainerWidthControl areaKey="banner" areaName="제목 헤더 영역" areaSelector="#banner-container" areaNumber={3} />
                    </div>
                  </div>
                </div>
              </div>

              {/* 업무화면 서브메뉴 영역 */}
              <div className="accordion-item" data-accordion-id="submenu">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeAccordion === 'submenu' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleAccordion('submenu')}
                  >
                    업무화면 서브메뉴 영역
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${activeAccordion === 'submenu' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <SubmenuSidebarControl />
                  </div>
                </div>
              </div>

              {/* 업무화면 메인 영역 */}
              <div className="accordion-item" data-accordion-id="content">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeAccordion === 'content' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleAccordion('content')}
                  >
                    업무화면 메인 영역
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${activeAccordion === 'content' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <ButtonThemeControl />
                    <AOSControl />
                    <ScrollToTopButtonControl />

                    <div style={{ marginTop: '15px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>📐 컨테이너 너비:</label>
                        <button
                          type="button"
                          onClick={() => {
                            const { set } = themeContext;
                            const areaVarNameMap = {
                              'header-top': '--container-max-width-header-top',
                              'header-bottom': '--container-max-width-header-bottom',
                              'banner': '--container-max-width-banner',
                              'content': '--container-max-width-content',
                              'footer': '--container-max-width-footer'
                            };
                            const varPrefix = areaVarNameMap['content'] || '--container-max-width-content';

                            // 각 사이즈별로 기본값으로 리셋
                            ['xl', 'lg', 'md', 'sm'].forEach(size => {
                              const varName = `${varPrefix}-${size}`;
                              document.documentElement.style.removeProperty(varName);
                              set(`areaContainerWidths.content.${size}`, null);
                            });
                          }}
                          style={{
                            background: 'transparent',
                            border: '1px solid #ddd',
                            color: '#666',
                            fontSize: '10px',
                            padding: '2px 8px',
                            borderRadius: '3px',
                            cursor: 'pointer'
                          }}
                          onMouseOver={(e) => {
                            e.target.style.backgroundColor = '#f5f5f5';
                            e.target.style.borderColor = themeConfig.themeColor || '#e97730';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.backgroundColor = 'transparent';
                            e.target.style.borderColor = '#ddd';
                          }}
                        >
                          리셋
                        </button>
                      </div>
                      <AreaContainerWidthControl areaKey="content" areaName="업무화면 영역" areaSelector=".main-wrapper" areaNumber={4} />
                    </div>
                  </div>
                </div>
              </div>

              {/* 연관사이트 영역 */}
              <div className="accordion-item" data-accordion-id="related-sites">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeAccordion === 'related-sites' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleAccordion('related-sites')}
                  >
                    연관사이트 영역
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${activeAccordion === 'related-sites' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <RelatedSitesControl />
                    {themeConfig.relatedSitesEnabled !== false && (
                      <AreaContainerWidthControl areaKey="related-sites" areaName="연관사이트 영역" areaSelector=".related-sites-section" areaNumber={6} />
                    )}
                  </div>
                </div>
              </div>

              {/* 하단 영역 */}
              <div className="accordion-item" data-accordion-id="footer">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeAccordion === 'footer' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleAccordion('footer')}
                  >
                    하단 영역
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${activeAccordion === 'footer' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <FooterEnabledControl />

                    {/* Footer 표시가 OFF이면 아래 테마 항목(디자인/테마/컨테이너 너비) 숨김 */}
                    {themeConfig.footerEnabled !== false && (
                      <>
                        <FooterControl />
                        <div style={{ marginTop: '15px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <label style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>📐 컨테이너 너비:</label>
                            <button
                              type="button"
                              onClick={() => {
                                const { set } = themeContext;
                                const areaVarNameMap = {
                                  'header-top': '--container-max-width-header-top',
                                  'header-bottom': '--container-max-width-header-bottom',
                                  'banner': '--container-max-width-banner',
                                  'content': '--container-max-width-content',
                                  'footer': '--container-max-width-footer'
                                };
                                const varPrefix = areaVarNameMap['footer'] || '--container-max-width-footer';

                                // 각 사이즈별로 기본값으로 리셋
                                ['xl', 'lg', 'md', 'sm'].forEach(size => {
                                  const varName = `${varPrefix}-${size}`;
                                  document.documentElement.style.removeProperty(varName);
                                  set(`areaContainerWidths.footer.${size}`, null);
                                });
                              }}
                              style={{
                                background: 'transparent',
                                border: '1px solid #ddd',
                                color: '#666',
                                fontSize: '10px',
                                padding: '2px 8px',
                                borderRadius: '3px',
                                cursor: 'pointer'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#f5f5f5';
                                e.target.style.borderColor = themeConfig.themeColor || '#e97730';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.borderColor = '#ddd';
                              }}
                            >
                              리셋
                            </button>
                          </div>
                          <AreaContainerWidthControl areaKey="footer" areaName="하단 푸터 영역" areaSelector=".footer-area" areaNumber={5} />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* 우측 사이드바 영역 */}
              <div className="accordion-item" data-accordion-id="right-sidebar">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeAccordion === 'right-sidebar' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleAccordion('right-sidebar')}
                  >
                    우측 사이드바 영역
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${activeAccordion === 'right-sidebar' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <RightSidebarTypeControl />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'overall-common' && (
            <div className="accordion" id="overall-common-accordion">
              {/* 글자 관련 */}
              <div className="accordion-item" data-accordion-id="fonts">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeAccordion === 'fonts' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleAccordion('fonts')}
                  >
                    글자 관련
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${activeAccordion === 'fonts' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <FontControl />
                  </div>
                </div>
              </div>

              {/* 색상 관련 */}
              <div className="accordion-item" data-accordion-id="colors">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeAccordion === 'colors' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleAccordion('colors')}
                  >
                    색상 관련
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${activeAccordion === 'colors' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <ColorControl />
                  </div>
                </div>
              </div>

              {/* 범위 관련 */}
              <div className="accordion-item" data-accordion-id="grid">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${activeAccordion === 'grid' ? '' : 'collapsed'}`}
                    type="button"
                    onClick={() => toggleAccordion('grid')}
                  >
                    범위 관련
                  </button>
                </h2>
                <div className={`accordion-collapse collapse ${activeAccordion === 'grid' ? 'show' : ''}`}>
                  <div className="accordion-body">
                    <div className="mb-3">
                      <div className="small text-secondary mb-2">전체 컨테이너 너비 설정 (containerWidths)</div>
                      <ContainerWidthControl />
                    </div>
                    <GridControl />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
};


export default ThemeRemoteControl;
